import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isAxiosError } from "axios";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import { TextInput } from "components/common/TextInput";
import { Button } from "components/common/Button";
import { TooltipButton } from "components/common/TooltipButton";
import { Chip, ChipVariant } from "components/common/Chip";
import { createLogger } from "debug/DebugingLogger";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { useSupportStore } from "stores/useSupportStore";
import { useSidebarOverlayStore } from "stores/useSidebarOverlayStore";
import { axiosInstance } from "contexts/axiosApiInstance";

const log = createLogger("SupportPanel");

interface LabelOption {
  value: string;
  label: string;
  icon: React.ReactNode;
  variant: ChipVariant;
  color: string;
}

const LABELS: LabelOption[] = [
  {
    value: "question",
    label: "Question",
    icon: <HelpOutlineIcon className="icon-xs" />,
    variant: "info",
    color: "var(--color-accent)",
  },
  {
    value: "feedback",
    label: "Feedback",
    icon: <ChatBubbleOutlineIcon className="icon-xs" />,
    variant: "success",
    color: "var(--color-success)",
  },
  {
    value: "bug",
    label: "Bug Report",
    icon: <BugReportOutlinedIcon className="icon-xs" />,
    variant: "error",
    color: "var(--color-error)",
  },
];

const SIDEBAR_FALLBACK = 48;

export const SupportPanel: React.FC = () => {
  const open = useSupportStore((s) => s.open);
  const anchorTop = useSupportStore((s) => s.anchorTop);
  const closeSupport = useSupportStore((s) => s.closeSupport);
  const pushOverlay = useSidebarOverlayStore((s) => s.push);
  const popOverlay = useSidebarOverlayStore((s) => s.pop);

  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const asideRef = useRef<HTMLElement>(null);
  // Hug the sidebar's right edge so the overlay reads as opening out of the
  // menu; `top` aligns with the trigger, clamped to stay on-screen.
  const [sidebarRight, setSidebarRight] = useState(SIDEBAR_FALLBACK);
  const [top, setTop] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      pushOverlay();
      return () => popOverlay();
    }
    setSelectedLabels([]);
    setSubject("");
    setMessage("");
    setTop(null);
  }, [open, pushOverlay, popOverlay]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) closeSupport();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeSupport]);

  useLayoutEffect(() => {
    if (!open) return;
    const sidebar = document.getElementById("main-sidebar");
    const place = () => {
      setSidebarRight(
        sidebar ? sidebar.getBoundingClientRect().right : SIDEBAR_FALLBACK,
      );
      const height = asideRef.current?.offsetHeight ?? 0;
      const margin = 8;
      const vh = window.innerHeight;
      const base = anchorTop ?? (vh - height) / 2;
      setTop(Math.max(margin, Math.min(base, vh - height - margin)));
    };
    place();
    window.addEventListener("resize", place);
    // Track the sidebar's collapse/expand animation so the overlay follows it.
    const observer = sidebar ? new ResizeObserver(place) : null;
    if (sidebar) observer?.observe(sidebar);
    return () => {
      window.removeEventListener("resize", place);
      observer?.disconnect();
    };
  }, [open, anchorTop]);

  const toggleLabel = (value: string) => {
    setSelectedLabels((prev) =>
      prev.includes(value) ? prev.filter((l) => l !== value) : [...prev, value],
    );
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      showNotificationGlobal(
        "Please fill in both subject and message",
        "error",
      );
      return;
    }
    setIsLoading(true);
    try {
      await axiosInstance.post("/action/support", {
        inquiry_type:
          selectedLabels.length > 0 ? selectedLabels.join(",") : "question",
        subject,
        message,
      });
      showNotificationGlobal("Support message sent successfully", "default");
      closeSupport();
    } catch (error: unknown) {
      log.error("Error sending support message: %o", error);
      const msg = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : "Failed to send support message";
      showNotificationGlobal(msg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-y-0 right-0 z-50"
        style={{ left: sidebarRight }}
        onClick={closeSupport}
        aria-hidden="true"
      />
      <div
        className="fixed inset-y-0 right-0 z-50 overflow-hidden pointer-events-none"
        style={{ left: sidebarRight }}
      >
        <aside
          ref={asideRef}
          className="absolute w-90 max-w-[80vw] max-h-[85vh] flex flex-col bg-bg-subtle border border-border rounded-app shadow-lg pointer-events-auto"
          style={{
            left: 8,
            top: top ?? anchorTop ?? 8,
            animation:
              "support-panel-slide-in 0.32s cubic-bezier(0.33, 1, 0.68, 1)",
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="support-panel-title"
        >
          <header className="flex items-center justify-between h-11 px-4 border-b border-border shrink-0">
            <h2 id="support-panel-title" className="text-fg-bold truncate">
              Contact Us
            </h2>
            <TooltipButton
              title="Close"
              variant="default"
              size="xs"
              onClick={closeSupport}
              aria-label="Close"
            >
              <CloseIcon />
            </TooltipButton>
          </header>

          <div className="flex-1 overflow-auto p-4 space-y-4">
            <p className="text-fg-muted-regular text-sm">
              We'd love to hear from you. Our team typically replies within 24
              hours.
            </p>
            <div>
              <label className="flex items-center text-fg-medium item-height">
                Subject
              </label>
              <TextInput
                value={subject}
                onChange={setSubject}
                placeholder="Enter your subject"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="flex items-center text-fg-medium item-height">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your message"
                disabled={isLoading}
                rows={6}
                className="input-custom-sm w-full resize-none"
                style={{
                  fontFamily: "var(--font-sans)",
                  padding: "8px 12px",
                  minHeight: "140px",
                }}
              />
            </div>
            <div>
              <label className="flex items-center text-fg-medium item-height">
                Label
              </label>
              <div
                className="flex flex-wrap"
                style={{ gap: "var(--spacing-xs)" }}
                role="group"
                aria-label="Label"
              >
                {LABELS.map((l) => {
                  const isSelected = selectedLabels.includes(l.value);
                  return (
                    <Chip
                      key={l.value}
                      label={
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "var(--spacing-xs)",
                          }}
                        >
                          <span
                            style={{
                              color: isSelected
                                ? l.color
                                : "var(--color-fg-muted)",
                              display: "inline-flex",
                            }}
                          >
                            {l.icon}
                          </span>
                          {l.label}
                        </span>
                      }
                      variant={l.variant}
                      active={isSelected}
                      onClick={() => toggleLabel(l.value)}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end px-4 py-3">
            <Button variant="primary" onClick={handleSend} loading={isLoading}>
              Send
            </Button>
          </div>
        </aside>
      </div>
    </>,
    document.body,
  );
};
